import { documentCountries, documentCountry, documentIdentityKey } from '@/utils/documentIdentity';
import { alwaysRetainsIrpf } from '@/utils/irpfPolicy';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import clientService from '../../services/clientService';
import { TipoCliente } from '@/types';
import Button from '@/components/common/Button';

// Uruguay departments and cities
const URUGUAY_DEPARTMENTS = [
  'Artigas', 'Canelones', 'Cerro Largo', 'Colonia', 'Durazno', 'Flores',
  'Florida', 'Lavalleja', 'Maldonado', 'Montevideo', 'Paysandú', 'Río Negro',
  'Rivera', 'Rocha', 'Salto', 'San José', 'Soriano', 'Tacuarembó', 'Treinta y Tres'
];

const URUGUAY_CITIES: Record<string, string[]> = {
  'Montevideo': ['Montevideo'],
  'Canelones': ['Canelones', 'Ciudad de la Costa', 'Las Piedras', 'Pando', 'Santa Lucía', 'Atlántida', 'Parque del Plata'],
  'Maldonado': ['Maldonado', 'Punta del Este', 'San Carlos', 'Pan de Azúcar'],
  'Salto': ['Salto'],
  'Paysandú': ['Paysandú', 'Guichón'],
  'Rivera': ['Rivera'],
  'Artigas': ['Artigas', 'Bella Unión'],
  'Tacuarembó': ['Tacuarembó'],
  'Colonia': ['Colonia del Sacramento', 'Carmelo', 'Nueva Helvecia', 'Juan Lacaze'],
  'Soriano': ['Mercedes', 'Dolores'],
  'Río Negro': ['Fray Bentos', 'Young'],
  'Durazno': ['Durazno'],
  'Flores': ['Trinidad'],
  'Florida': ['Florida'],
  'Cerro Largo': ['Melo', 'Río Branco'],
  'Treinta y Tres': ['Treinta y Tres'],
  'Lavalleja': ['Minas'],
  'Rocha': ['Rocha', 'La Paloma', 'Chuy'],
  'San José': ['San José de Mayo', 'Ciudad del Plata', 'Libertad']
};

// Countries list (prioritizing requested ones)
const COUNTRIES = [
  'Uruguay',
  'Argentina',
  'Brazil',
  '---', // Separator
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Armenia', 'Australia',
  'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brunei', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Central African Republic',
  'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus',
  'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'East Timor', 'Ecuador',
  'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Ethiopia', 'Fiji', 'Finland',
  'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea',
  'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran',
  'Iraq', 'Ireland', 'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan',
  'Kenya', 'Kiribati', 'North Korea', 'South Korea', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon',
  'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Macedonia', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
  'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique',
  'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria',
  'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis',
  'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
  'Swaziland', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Uzbekistan', 'Vanuatu', 'Vatican City',
  'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

const ClientForm: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState<any>({
    tipo_documento: 'CI',
    documento: '',
    tipo: 'PROPIETARIO',
    primer_nombre: '',
    segundo_nombre: '',
    primer_apellido: '',
    segundo_apellido: '',
    nacionalidad: 'Uruguay',
    residencia: 'Uruguay', // Meeting 10-02-26: Residency for IRPF purposes
    retiene_irpf: false as boolean | null,
    agente_retencion_irpf: false,
    email: '',
    telefono: '',
    direccion: '',
    numero_puerta: '',
    ciudad: '',
    departamento: '',
    profesion: '',
    notas: '',
    activo: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [initialDocumentKey, setInitialDocumentKey] = useState<string | null>(null);
  const [hasOwnerLinks, setHasOwnerLinks] = useState(false);
  const [hasTenantLinks, setHasTenantLinks] = useState(false);
  const [initialType, setInitialType] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (isEdit) {
        setLoading(true);
        try {
          const res: any = await clientService.getClientById(Number(id));
          const clientData = res.data;
          setInitialDocumentKey(documentIdentityKey(clientData));
          setForm({
            ...clientData,
            segundo_nombre: clientData.segundo_nombre || '',
            segundo_apellido: clientData.segundo_apellido || '',
            residencia: clientData.residencia || 'Uruguay',
            retiene_irpf: alwaysRetainsIrpf(clientData),
            agente_retencion_irpf: !!clientData.agente_retencion_irpf,
          });
          setHasOwnerLinks(Boolean(clientData.has_owner_links));
          setHasTenantLinks(Boolean(clientData.has_tenant_links));
          setInitialType(clientData.tipo);
        } catch (e: any) {
          setError(e?.response?.data?.message || 'Error al cargar el cliente');
        } finally {
          setLoading(false);
        }
      }
    };
    load();
  }, [id]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!form.primer_nombre?.trim()) {
      errors.primer_nombre = 'El primer nombre es requerido';
    }
    if (!form.primer_apellido?.trim()) {
      errors.primer_apellido = 'El primer apellido es requerido';
    }
    if (!form.tipo_documento) {
      errors.tipo_documento = 'El tipo de documento es requerido';
    }
    if (!form.documento?.trim()) {
      errors.documento = 'El número de documento es requerido';
    }
    const identityChanged = !isEdit || initialDocumentKey !== documentIdentityKey(form);
    if (identityChanged && !['CI', 'RUT'].includes(form.tipo_documento)) {
      if (!documentCountry(form)) {
        errors.nacionalidad = 'Seleccione una nacionalidad reconocida para generar CFE con documentos extranjeros';
      }
      if (Array.from(form.documento.trim()).length > 20) {
        errors.documento = 'El documento admite hasta 20 caracteres';
      }
    }
    if (!form.tipo) {
      errors.tipo = 'El tipo de cliente es requerido';
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = 'El email no tiene un formato válido';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!validateForm()) {
      setError('Por favor corrija los errores del formulario');
      return;
    }

    setLoading(true);
    try {
      if (isEdit) {
        await clientService.updateClient(Number(id), form);
        setMessage('Cliente actualizado exitosamente');
      } else {
        await clientService.createClient(form);
        setMessage('Cliente creado exitosamente');
      }
      setTimeout(() => navigate('/clients'), 1000);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al guardar el cliente');
      setValidationErrors(e?.response?.data?.errors || {});
    } finally {
      setLoading(false);
    }
  };

  const filteredCountries = COUNTRIES.filter(country =>
    country.toLowerCase().includes(countrySearch.toLowerCase()) || country === '---'
  );

  const availableCities = form.departamento ? URUGUAY_CITIES[form.departamento] || [] : [];

  return (
    <MainLayout>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <Link to="/clients" className="hover:text-gray-900">Clientes</Link>
            <span>/</span>
            <span className="text-gray-900">{isEdit ? 'Editar Cliente' : 'Nuevo Cliente'}</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'Editar Cliente' : 'Nuevo Cliente'}
          </h1>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}
        {message && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">{message}</p>
          </div>
        )}

        {loading && !form.primer_nombre ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
              <p className="text-gray-600">Cargando...</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Section: Información Personal */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">
                Información Personal
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Primer Nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      validationErrors.primer_nombre ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={form.primer_nombre}
                    onChange={(e) => {
                      setForm({ ...form, primer_nombre: e.target.value });
                      setValidationErrors({ ...validationErrors, primer_nombre: '' });
                    }}
                    placeholder="Ej: Juan"
                  />
                  {validationErrors.primer_nombre && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.primer_nombre}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Segundo Nombre
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.segundo_nombre || ''}
                    onChange={(e) => setForm({ ...form, segundo_nombre: e.target.value })}
                    placeholder="Ej: Carlos"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Primer Apellido <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      validationErrors.primer_apellido ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={form.primer_apellido}
                    onChange={(e) => {
                      setForm({ ...form, primer_apellido: e.target.value });
                      setValidationErrors({ ...validationErrors, primer_apellido: '' });
                    }}
                    placeholder="Ej: Pérez"
                  />
                  {validationErrors.primer_apellido && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.primer_apellido}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Segundo Apellido
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.segundo_apellido || ''}
                    onChange={(e) => setForm({ ...form, segundo_apellido: e.target.value })}
                    placeholder="Ej: González"
                  />
                </div>

                <div className="relative">
                  <label htmlFor="nacionalidad" className="block text-sm font-medium text-gray-700 mb-1">
                    Nacionalidad
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.nacionalidad || ''}
                    onChange={(e) => {
                      setForm({ ...form, nacionalidad: e.target.value });
                      setCountrySearch(e.target.value);
                      setShowCountryDropdown(true);
                    }}
                    onFocus={() => setShowCountryDropdown(true)}
                    onBlur={() => {
                      // Delay to allow click events on dropdown items to fire first
                      setTimeout(() => setShowCountryDropdown(false), 200);
                    }}
                    id="nacionalidad"
                    aria-invalid={Boolean(validationErrors.nacionalidad)}
                    placeholder="Buscar país..."
                  />
                  {validationErrors.nacionalidad && <p role="alert" className="text-red-500 text-xs mt-1">{validationErrors.nacionalidad}</p>}
                  {showCountryDropdown && (
                    <div
                      className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                      onMouseDown={(e) => {
                        // Prevent blur event when clicking on dropdown
                        e.preventDefault();
                      }}
                    >
                      {filteredCountries.map((country, idx) =>
                        country === '---' ? (
                          <div key={idx} className="border-t border-gray-200 my-1"></div>
                        ) : (
                          <div
                            key={country}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                            onClick={() => {
                              setForm({ ...form, nacionalidad: country });
                              setCountrySearch(country);
                              setShowCountryDropdown(false);
                            }}
                          >
                            {country}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profesión
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.profesion || ''}
                    onChange={(e) => setForm({ ...form, profesion: e.target.value })}
                    placeholder="Ej: Ingeniero, Docente, etc."
                  />
                </div>
              </div>
            </div>

            {/* Section: Documentación */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">
                Documentación
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="tipo-documento" className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Documento <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      validationErrors.tipo_documento ? 'border-red-500' : 'border-gray-300'
                    }`}
                    id="tipo-documento"
                    value={form.tipo_documento}
                    onChange={(e) => {
                      setForm({ ...form, tipo_documento: e.target.value });
                      setValidationErrors({ ...validationErrors, tipo_documento: '' });
                    }}
                  >
                    <option value="CI">CI (Cédula de Identidad)</option>
                    <option value="RUT">RUT</option>
                    <option value="Pasaporte">Pasaporte</option>
                    <option value="Otros">Otros</option>
                  </select>
                  {validationErrors.tipo_documento && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.tipo_documento}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="numero-documento" className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Documento <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      validationErrors.documento ? 'border-red-500' : 'border-gray-300'
                    }`}
                    id="numero-documento"
                    value={form.documento}
                    onChange={(e) => {
                      setForm({ ...form, documento: e.target.value });
                      setValidationErrors({ ...validationErrors, documento: '' });
                    }}
                    placeholder="Ingrese el número de documento"
                  />
                  {validationErrors.documento && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.documento}</p>
                  )}
                </div>

                <p className="text-sm text-gray-500 md:col-span-2">
                  País para CFE: {documentCountries[documentCountry(form)] || 'Complete la nacionalidad'}.
                  {' '}Se determina automáticamente a partir del tipo de documento y la nacionalidad.
                </p>

                <div>
                  <label htmlFor="tipo_cliente" className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Cliente <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="tipo_cliente"
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      validationErrors.tipo ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={form.tipo}
                    onChange={(e) => {
                      setForm({ ...form, tipo: e.target.value, agente_retencion_irpf: e.target.value === TipoCliente.PROPIETARIO ? false : form.agente_retencion_irpf });
                      setValidationErrors({ ...validationErrors, tipo: '' });
                    }}
                  >
                    <option
                      value={TipoCliente.PROPIETARIO}
                      disabled={isEdit && hasTenantLinks && initialType !== TipoCliente.PROPIETARIO}
                      className={isEdit && hasTenantLinks && initialType !== TipoCliente.PROPIETARIO ? 'text-gray-400' : ''}
                    >
                      Propietario
                    </option>
                    <option
                      value={TipoCliente.INQUILINO}
                      disabled={isEdit && hasOwnerLinks && initialType !== TipoCliente.INQUILINO}
                      className={isEdit && hasOwnerLinks && initialType !== TipoCliente.INQUILINO ? 'text-gray-400' : ''}
                    >
                      Inquilino
                    </option>
                    <option value={TipoCliente.AMBOS}>Ambos</option>
                  </select>
                  {isEdit && hasOwnerLinks && initialType !== TipoCliente.INQUILINO && (
                    <p className="text-gray-500 text-xs mt-1">
                      No se puede seleccionar <strong>Inquilino</strong> mientras el cliente siga vinculado como propietario en inmuebles o contratos activos. Seleccione <strong>Ambos</strong> si también es inquilino.
                    </p>
                  )}
                  {isEdit && hasTenantLinks && initialType !== TipoCliente.PROPIETARIO && (
                    <p className="text-gray-500 text-xs mt-1">
                      No se puede seleccionar <strong>Propietario</strong> mientras el cliente tenga contratos activos como inquilino. Seleccione <strong>Ambos</strong> si también es propietario.
                    </p>
                  )}
                  {validationErrors.tipo && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.tipo}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Section: Información de Contacto */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">
                Información de Contacto
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      validationErrors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={form.email || ''}
                    onChange={(e) => {
                      setForm({ ...form, email: e.target.value });
                      setValidationErrors({ ...validationErrors, email: '' });
                    }}
                    placeholder="correo@ejemplo.com"
                  />
                  {validationErrors.email && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.telefono || ''}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    placeholder="099 123 456"
                  />
                </div>
              </div>
            </div>

            {/* Section: Dirección */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">
                Dirección
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Departamento
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.departamento || ''}
                    onChange={(e) => {
                      setForm({ ...form, departamento: e.target.value, ciudad: '' });
                    }}
                  >
                    <option value="">Seleccione un departamento</option>
                    {URUGUAY_DEPARTMENTS.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ciudad
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.ciudad || ''}
                    onChange={(e) => setForm({ ...form, ciudad: e.target.value.toLowerCase().trim() })}
                    placeholder="Ej: montevideo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dirección (Calle)
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.direccion || ''}
                    onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                    placeholder="Ej: Av. 18 de Julio"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Puerta
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.numero_puerta || ''}
                    onChange={(e) => setForm({ ...form, numero_puerta: e.target.value })}
                    placeholder="Ej: 1234 Apto 5"
                  />
                </div>
              </div>
            </div>

            {/* Section: Configuración IRPF (only for owners) */}
            {(form.tipo === TipoCliente.PROPIETARIO || form.tipo === TipoCliente.AMBOS) && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">
                  Configuración IRPF
                </h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    Esta configuración aplica a todos los inmuebles de este propietario.
                    Si la residencia es fuera de Uruguay, se activa automáticamente la retención de IRPF.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      País de Residencia
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.residencia || 'Uruguay'}
                      onChange={(e) => {
                        const newResidencia = e.target.value;
                        // Auto-set retiene_irpf when residency is not Uruguay
                        const autoRetiene = newResidencia !== 'Uruguay';
                        setForm({
                          ...form,
                          residencia: newResidencia,
                          retiene_irpf: autoRetiene || form.retiene_irpf
                        });
                      }}
                    >
                      {COUNTRIES.map((country, idx) =>
                        country === '---' ? (
                          <option key={idx} disabled>────────────</option>
                        ) : (
                          <option key={country} value={country}>{country}</option>
                        )
                      )}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Los no residentes tienen retención obligatoria de IRPF
                    </p>
                  </div>

                  <div className="pt-2">
                    <div>
                      <label htmlFor="retiene_irpf" className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input id="retiene_irpf" type="checkbox"
                          checked={alwaysRetainsIrpf(form)}
                          onChange={(e) => setForm({ ...form, retiene_irpf: e.target.checked })}
                          disabled={form.residencia !== 'Uruguay'}
                        />
                        Siempre Retener IRPF
                      </label>
                      <p className="text-sm text-gray-600 mt-2">
                        Desmarcado: se retiene cuando la porción mensual del propietario alcanza el mínimo de su moneda.
                        Marcado: se retiene también por debajo del mínimo, en todos sus contratos.
                      </p>
                    </div>
                  </div>
                </div>
                {form.residencia !== 'Uruguay' && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Nota:</strong> Como propietario no residente en Uruguay,
                      la retención de IRPF se aplica obligatoriamente sin importar el monto del alquiler.
                    </p>
                  </div>
                )}
              </div>
            )}

            {(form.tipo === TipoCliente.INQUILINO || form.tipo === TipoCliente.AMBOS) && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">IRPF del inquilino</h2>
                <label htmlFor="agente_retencion_irpf" className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input id="agente_retencion_irpf" type="checkbox" checked={!!form.agente_retencion_irpf}
                    onChange={e => setForm({ ...form, agente_retencion_irpf: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  El inquilino actúa como agente de retención de IRPF
                </label>
                <p className="text-sm text-gray-600 mt-2">Aplica a sus contratos como inquilino. La retención se descuenta del cobro y la comisión se calcula sobre el importe restante. El resguardo se registra en cada recibo.</p>
                {isEdit && <p className="text-sm text-gray-600 mt-2">Al cambiar esta opción se recalculan las liquidaciones futuras pendientes sin cobros registrados. Los recibos emitidos conservan su información.</p>}
              </div>
            )}

            {/* Section: Información Adicional */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">
                Información Adicional
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    value={form.notas || ''}
                    onChange={(e) => setForm({ ...form, notas: e.target.value })}
                    placeholder="Información adicional sobre el cliente..."
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    id="active"
                    type="checkbox"
                    checked={!!form.activo}
                    onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-gray-700">
                    Cliente activo
                  </label>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/clients')}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
              >
                {loading ? 'Guardando...' : isEdit ? 'Actualizar Cliente' : 'Crear Cliente'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </MainLayout>
  );
};

export default ClientForm;
